// FirewallMain.tsx
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMemo, useEffect, useState } from 'react';
import { FirewallSidebar, FIREWALL_TABS } from './FirewallSidebar';

import { ArrowBigLeft, Fingerprint, Shield } from 'lucide-react';
import FirewallOverview from './FirewallOverview';
import ServerProtocols from '../ServerProtocols';
import FirewallInterface from './FirewallInterface';
import FirewallRules from './FirewallRules';
import VpnMain from './Vpn/VpnMain';
import FirewallNetworkMain from './FirewallNetwork/FirewallNetworkMain';
import FirewallObjetcsMain from './FirewallObjects/FirewallObjetcsMain';
import FirewallCertificates from './FirewallCertificates';
import FirewallCompliance from './FirewallCompliance';
import TrafficAnalytics from './TrafficAnalytics';
import SecurityProfilesMain from './SecurityProfiles/SecurityProfilesMain';
import HardwareOverview from './HardwareOverview';
import LogsMain from './FirewallLogs/LogsMain';
import FirewallLogs from './FirewallLogs/FirewallLogs';
import FirewallUsers from './FirewallUsers';
import FirewallWebFiltersMain from './FirewallWebFilters/FirewallWebFiltersMain';
import FirewallLicenses from './FirewallLicenses';
import FirewallSSH from './FirewallSSH';


export default function FirewallMain() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [fingerprintMode, setFingerprintMode] = useState(false);

    const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
    const asset_id = info.asset_id;
    const name = info.name;
    const client_id = info.client_id;
    const path = info.path;

    const requestedTab = searchParams.get('tab');

    const activeTab = useMemo(() => {
        const isValid = FIREWALL_TABS.some((t) => t.id === requestedTab);
        return isValid && requestedTab ? requestedTab : FIREWALL_TABS[0].id;
    }, [requestedTab]);

    const TAB_COMPONENTS: Record<string, React.ReactNode> = {
        "overview": <FirewallOverview asset_id={asset_id} client_id={client_id} onDataLoaded={(hasData) => setFingerprintMode(hasData)} />,
        "ssh": <FirewallSSH asset_id={asset_id} client_id={client_id} />,
        "firewall-logs": <FirewallLogs asset_id={asset_id} client_id={client_id} />,
        "interface": <FirewallInterface asset_id={asset_id} client_id={client_id} />,
        "rules": <FirewallRules asset_id={asset_id} client_id={client_id} />,
        "vpn": <VpnMain asset_id={asset_id} client_id={client_id} />,
        "objects": <FirewallObjetcsMain asset_id={asset_id} client_id={client_id} />,
        "network": <FirewallNetworkMain asset_id={asset_id} client_id={client_id} />,
        "certificate": <FirewallCertificates asset_id={asset_id} client_id={client_id} />,
        "compliance": <FirewallCompliance asset_id={asset_id} client_id={client_id} />,
        "traffic-analytics": <TrafficAnalytics asset_id={asset_id} client_id={client_id} />,
        "security-profiles": <SecurityProfilesMain asset_id={asset_id} client_id={client_id} />,
        "hardware": <HardwareOverview asset_id={asset_id} client_id={client_id} />,
        "logs": <LogsMain asset_id={asset_id} client_id={client_id} />,
        "users": <FirewallUsers asset_id={asset_id} client_id={client_id} />,
        "web-filters": <FirewallWebFiltersMain asset_id={asset_id} client_id={client_id} />,
        "license": <FirewallLicenses asset_id={asset_id} client_id={client_id} />,
    };

    useEffect(() => {
        if (requestedTab !== activeTab) {
            setSearchParams({ tab: activeTab }, { replace: true });
        }
    }, [activeTab, requestedTab, setSearchParams]);



    return (
        <div className="flex w-full bg-gray-900 text-gray-200 h-[calc(100vh-1rem)] overflow-hidden">


            {/* Sidebar */}

            {!fingerprintMode && (
                <FirewallSidebar
                    tabs={FIREWALL_TABS}
                    activeTab={activeTab}
                    onTabChange={(id) => setSearchParams({ tab: id })}
                />
            )}

            {/* Main Content */}
            <div className="flex-1 min-w-0 min-h-0 bg-gray-900 flex flex-col overflow-hidden">
                {/* Sticky Header */}
                <div className="flex justify-between border-b border-gray-800 bg-gray-900 px-2 py-1.5 shrink-0">                    <div className="flex items-center gap-2">
                    <div className="bg-orange-600/20 p-1.5 rounded-lg border border-orange-500/30 shrink-0">
                        <Shield className="text-orange-500 w-4 h-4" />
                    </div>
                    <h1 className="text-sm font-bold text-white tracking-wide">{name}</h1>
                </div>
                    <div className='flex gap-2'>

                        <div className="flex items-center gap-2 bg-slate-900/50 p-1 rounded-lg border border-slate-800/60 mr-2">
                            <div className={`p-1.5 rounded-md transition-colors ${fingerprintMode ? 'bg-orange-600 text-white' : 'text-slate-500'}`}>
                                <Fingerprint className="w-3.5 h-3.5" />
                            </div>
                            <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Fingerprint</span>
                            <button
                                onClick={() => setFingerprintMode(!fingerprintMode)}
                                className={`relative inline-flex h-5 w-10 items-center rounded-full transition-all duration-300 focus:outline-none ${fingerprintMode ? 'bg-orange-600' : 'bg-slate-700'}`}
                            >
                                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-300 ${fingerprintMode ? 'translate-x-5.5' : 'translate-x-1'}`} />
                            </button>
                        </div>

                        <button
                            className="bg-orange-700 hover:bg-orange-600 flex items-center gap-1 text-white px-4 py-2 rounded-lg"
                            onClick={() => navigate(path)}
                        >
                            <ArrowBigLeft className="w-4 h-4" />
                            <span className="text-xs font-semibold">Back</span>
                        </button>
                    </div>

                </div>

                {/* Tab Content */}

                {fingerprintMode ?
                    <div className="flex-1 min-h-0 pb-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <ServerProtocols hideHeader={false} />
                    </div>
                    :
                    <div className="w-full pb-12 px-4 py-2 overflow-y-auto custom-scroll pb-20">
                        {TAB_COMPONENTS[activeTab]}
                    </div>
                }

            </div>
        </div>
    );
}
